import { debounce } from 'lodash';
import { TriangleAlert } from 'lucide-react';

import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LegalContextEnum, type IAgreementUpdateParams } from 'src/graphql/schema';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { legalContextOptionsRich as options } from '../model';

type Props = {
  onUpdate: (agreementParams: IAgreementUpdateParams) => void;
  onOtherChange: (value: string) => void;
  legalContext?: LegalContextEnum[];
  legalContextOther?: string;
  isOtherInvalid?: boolean;
};

const OTHER_ERROR_ID = 'legalContextOther-error';

const AgreementLegalContextSettings = ({
  onUpdate,
  legalContext = [],
  onOtherChange,
  legalContextOther,
  isOtherInvalid = false,
}: Props) => {
  const onLegalContextChange = (legalContext: LegalContextEnum[]) => onUpdate({ legalContext });

  const onLegalOtherChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => onOtherChange(event.target.value),
    500,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Legal context</CardTitle>
        <CardDescription>
          Choose or enter the title of the agreement between you and your counterpart. This is the legal contract
          outlining your relationship and obligations and will be referenced in the escrow agreement.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ToggleGroupCard type="multiple" onValueChange={onLegalContextChange} value={legalContext}>
          {options.map(({ title, value, description, icon }) => (
            <ToggleGroupCardItem key={value} value={value} icon={icon} title={title} description={description} />
          ))}
        </ToggleGroupCard>

        {legalContext.includes(LegalContextEnum.Other) && (
          <div className="mt-6">
            <Label htmlFor="legalContextOther">Your agreement title</Label>
            <Input
              id="legalContextOther"
              placeholder="e.g. Reseller Agreement"
              onChange={onLegalOtherChange}
              defaultValue={legalContextOther || ''}
              aria-invalid={isOtherInvalid}
              aria-describedby={isOtherInvalid ? OTHER_ERROR_ID : undefined}
            />
            {isOtherInvalid && (
              <p id={OTHER_ERROR_ID} className="mt-2 flex items-start gap-1.25 text-xs text-destructive">
                <TriangleAlert className="size-3.5 shrink-0" />
                Enter your agreement title.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgreementLegalContextSettings;
